/**
 * SaltieShoutOutStationery — the featured Saltie Shout-Out rendered as
 * a real HTML stationery card. This is the READABLE version of what is
 * painted inside the shutter display in the hero above.
 *
 * DESIGN INTENT
 * =============
 * The stationery is treated as a real, tangible object pinned to the
 * shutter wall — NOT painted flat onto it. It has:
 *   • a warm cream cotton-paper body that is the BRIGHTEST object on
 *     the page (the letter is the visual anchor);
 *   • subtle deckled/uneven edges (torn cotton paper feel);
 *   • a faint letterpress inner border;
 *   • an optional 5%-opacity round Salt of the Herd seal watermark
 *     centered behind the text — almost invisible;
 *   • a real drop shadow + a tiny corner curl at bottom-right so it
 *     feels pinned to the wall, not printed;
 *   • generous breathing room between the letter body and the
 *     handwritten sign-off (white space is emotional);
 *   • a BLIND-EMBOSSED Salt of the Herd seal at the bottom (raised
 *     paper impression, no ink), like premium stationery;
 *   • an OPTIONAL keepsake recipient photo tucked under the top-left
 *     corner with a small brass clip.
 *
 * Props allow the admin editor to swap the recipient, body, photo,
 * and closing date without touching layout or typography. The sign-off
 * ("With Love," → "Stefanie ♡" → "The Salty Moo") is FIXED and rendered
 * from Stefanie's actual handwriting extractions.
 */
import {
  HandwrittenTitle,
  HandwrittenBody,
  HandwrittenClosing,
} from "./StefaniesHandwriting";

const DEFAULTS = {
  recipient: "Friend",
  body:
    "You may never know how much your kindness meant. In a season when the days felt heavy, your quiet encouragement lifted my heart and reminded me that God still weaves small miracles through willing hands.\n\nThank you for showing up, for cheering me on, and for believing in this little dream when it was still just a whisper. You are one of the reasons The Salty Moo has a voice at all.",
};

/**
 * <KeepsakePhoto /> — a slightly worn, warm-toned print of the
 * recipient photo, tucked under the top-left corner of the stationery
 * and held in place with a small antique brass clip. Rendered only
 * when `photoUrl` is provided.
 */
function KeepsakePhoto({ photoUrl, alt = "" }) {
  if (!photoUrl) return null;
  return (
    <div
      aria-hidden={false}
      className="absolute pointer-events-none"
      style={{
        top: "-22px",
        left: "-32px",
        width: "132px",
        transform: "rotate(-6deg)",
        transformOrigin: "top left",
        filter: "drop-shadow(0 10px 12px rgba(40,25,10,0.35))",
      }}
      data-testid="keepsake-photo"
    >
      {/* Antique brass clip — a tiny hinged bulldog-clip silhouette
          sitting over the top-center of the photo. Pure CSS/SVG so it
          scales cleanly and never pixelates. */}
      <svg
        width="42"
        height="26"
        viewBox="0 0 42 26"
        aria-hidden="true"
        style={{
          position: "absolute",
          top: "-14px",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 3,
          filter: "drop-shadow(0 2px 2px rgba(60,40,20,0.4))",
        }}
      >
        <defs>
          <linearGradient id="brass-body" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#E7C078" />
            <stop offset="0.5" stopColor="#C69A4C" />
            <stop offset="1" stopColor="#8B6222" />
          </linearGradient>
          <linearGradient id="brass-lever" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#F2D391" />
            <stop offset="1" stopColor="#A97A32" />
          </linearGradient>
        </defs>
        {/* Body of the clip */}
        <rect x="4" y="10" width="34" height="12" rx="1.5" fill="url(#brass-body)" stroke="#5F4114" strokeWidth="0.6" />
        {/* Levers/handles */}
        <path d="M8 10 L4 2 L11 6 Z" fill="url(#brass-lever)" stroke="#5F4114" strokeWidth="0.5" />
        <path d="M34 10 L38 2 L31 6 Z" fill="url(#brass-lever)" stroke="#5F4114" strokeWidth="0.5" />
        {/* Highlight along the top edge of the body */}
        <rect x="4" y="10" width="34" height="1.2" fill="rgba(255,240,190,0.55)" />
      </svg>

      {/* Photo card — warm cream matte with slightly worn edges. */}
      <div
        className="relative"
        style={{
          padding: "8px 8px 22px",
          background: "linear-gradient(180deg, #FFF6DE 0%, #F4E3B7 100%)",
          borderRadius: "2px",
          boxShadow:
            "0 1px 0 rgba(255,255,240,0.9) inset, 0 -1px 0 rgba(120,80,40,0.25) inset, 0 3px 6px rgba(60,40,20,0.15)",
          // Worn-edge feel via a subtle displacement filter reused from
          // the parchment card's own defs (this component is always
          // rendered inside <SaltieShoutOutStationery>).
          filter: "url(#sso-deckle-filter)",
        }}
      >
        <img
          src={photoUrl}
          alt={alt}
          draggable="false"
          style={{
            display: "block",
            width: "100%",
            aspectRatio: "1 / 1",
            objectFit: "cover",
            // Warm sepia-ish tone so the photo reads as a keepsake, not
            // a smartphone shot. Very subtle.
            filter: "sepia(0.18) saturate(0.9) contrast(0.95) brightness(1.02)",
          }}
        />
      </div>
    </div>
  );
}

export default function SaltieShoutOutStationery({
  recipient = DEFAULTS.recipient,
  body = DEFAULTS.body,
  photoUrl = null,
  photoAlt = "",
  closingDate = "",
}) {
  return (
    <div
      className="mx-auto relative"
      style={{ maxWidth: "42rem", width: "100%" }}
      data-testid="saltie-shout-out-stationery"
    >
      {/* Inline SVG defs (deckle mask) — kept scoped to this component. */}
      <svg
        aria-hidden="true"
        width="0"
        height="0"
        style={{ position: "absolute", pointerEvents: "none" }}
      >
        <defs>
          <filter id="sso-deckle-filter">
            <feTurbulence
              type="fractalNoise"
              baseFrequency="0.018"
              numOctaves="2"
              seed="7"
              result="noise"
            />
            <feDisplacementMap in="SourceGraphic" in2="noise" scale="3" />
          </filter>
        </defs>
      </svg>

      {/* Outer tilt wrapper — a tiny rotation makes the letter feel
          hand-pinned instead of digitally placed. */}
      <div className="relative" style={{ transform: "rotate(-0.45deg)" }}>
        {/* Drop shadow lift layer — sits BEHIND the paper and gives it
            the "pinned to the wall" separation without darkening the
            paper itself. */}
        <div
          aria-hidden="true"
          className="absolute inset-0 rounded-md"
          style={{
            boxShadow:
              "0 30px 55px -22px rgba(40,25,10,0.45), 0 10px 22px -6px rgba(40,25,10,0.28), 0 2px 4px rgba(40,25,10,0.12)",
            borderRadius: "6px",
            transform: "translateY(2px)",
          }}
        />

        {/* PAPER — the brightest object on the page. */}
        <div
          className="relative px-8 py-12 sm:px-14 sm:py-16"
          style={{
            background:
              "radial-gradient(ellipse at 50% 18%, #FFF7E1 0%, #FDECC6 55%, #F6DDAC 100%)",
            borderRadius: "6px",
            filter: "url(#sso-deckle-filter)",
            boxShadow:
              "inset 0 0 0 1px rgba(168,113,73,0.28), inset 0 0 0 6px rgba(255,247,225,0.35), inset 0 0 60px rgba(255,236,196,0.55)",
          }}
        >
          {/* Faint letterpress inner border. */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute rounded-[4px]"
            style={{
              top: "12px",
              left: "12px",
              right: "12px",
              bottom: "12px",
              border: "1px solid rgba(168,113,73,0.22)",
              boxShadow: "inset 0 0 0 1px rgba(255,247,225,0.55)",
            }}
          />

          {/* Optional closing date (small caps eyebrow, top-right). */}
          {closingDate ? (
            <p
              className="text-[10px] uppercase tracking-[0.34em] text-[#7E9268] mb-3 text-right relative"
              data-testid="stationery-closing-date"
            >
              {closingDate}
            </p>
          ) : null}

          {/* ROUND SALT OF THE HERD SEAL — the official stationery
              logo, kept exactly as it is. Sits at the top as the
              visual anchor of every Saltie Shout-Out. */}
          <div className="flex justify-center relative" data-testid="stationery-top-seal">
            <img
              src="/assets/sm-circle-seal.png"
              alt="Salt of the Herd — The Salty Moo"
              draggable="false"
              style={{
                width: "clamp(72px, 8vw, 96px)",
                height: "auto",
                display: "block",
              }}
            />
          </div>

          {/* PRINTED MASTHEAD — small-caps letterhead labels below the
              round seal. Quiet weight; anchors the letterhead without
              competing with the title. */}
          <div className="text-center relative mt-3" data-testid="stationery-masthead">
            <p
              className="uppercase text-[#8B6F4E]"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "clamp(9px, 0.9vw, 11px)",
                letterSpacing: "0.42em",
                lineHeight: 1.4,
                marginBottom: "3px",
              }}
            >
              From the Heart of the Herd<sup className="align-super">&trade;</sup>
            </p>
            <p
              className="uppercase text-[#6E4E2E]"
              style={{
                fontFamily: "'Cormorant Garamond', serif",
                fontSize: "clamp(10px, 1.05vw, 13px)",
                letterSpacing: "0.34em",
                lineHeight: 1.4,
                fontWeight: 600,
              }}
            >
              A Saltie Shout-Out
            </p>
          </div>

          {/* PERMANENT LETTER TITLE — typeset in Bakerie. Same phrase
              on every Saltie Shout-Out. Sized as an elegant personal
              inscription. */}
          <div className="relative mt-5">
            <HandwrittenTitle />
          </div>

          {/* Body — flows directly under the title. The greeting has
              been intentionally removed so the reader eases straight
              into the message. */}
          <div className="relative mt-8 sm:mt-10">
            <HandwrittenBody text={body} />
          </div>

          {/* Closing — kept close to the body so it feels connected
              rather than separated by a large void. Same Bakerie
              hierarchy: With Love, → Stefanie → The Salty Moo™. */}
          <div className="mt-8 sm:mt-10 flex justify-end relative">
            <HandwrittenClosing />
          </div>
        </div>

        {/* Corner curl — a tiny lifted paper flap in the bottom-right,
            with its own cast shadow so the card feels like it's coming
            off the wall a hair. */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute"
          style={{
            right: "-2px",
            bottom: "-2px",
            width: "48px",
            height: "48px",
          }}
        >
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "radial-gradient(circle at 100% 100%, rgba(40,25,10,0.35), rgba(40,25,10,0) 60%)",
              transform: "translate(4px, 4px)",
              borderRadius: "6px",
            }}
          />
          <div
            style={{
              position: "absolute",
              inset: 0,
              background:
                "linear-gradient(135deg, transparent 50%, #FFF3D2 50%, #F1D8A3 100%)",
              boxShadow: "inset 0 0 6px rgba(168,113,73,0.35)",
              borderBottomRightRadius: "6px",
            }}
          />
        </div>

        {/* Keepsake recipient photo — pinned by a small brass clip to
            the top-left corner of the stationery. Rendered LAST so it
            sits on top of the paper edge and the drop-shadow layer. */}
        <KeepsakePhoto photoUrl={photoUrl} alt={photoAlt} />
      </div>
    </div>
  );
}
